import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Check, Clock, Users, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuggestedDevotees() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/friends/suggestions?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(res.data);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendRequest = async (userId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/request/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(prev => prev.map(s =>
        s.id === userId ? { ...s, friendship_status: 'requested' } : s
      ));
      toast.success('Devotee request sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="w-9 h-9 rounded-full bg-gray-200" />
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded w-24 mb-1" />
              <div className="h-2 bg-gray-200 rounded w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[17px] font-semibold text-gray-500">People You May Know</h3>
        <button
          onClick={() => navigate('/discover')}
          className="text-sm text-[#FF9933] hover:text-[#E68A00] font-medium"
          data-testid="see-all-suggestions"
        >
          See All
        </button>
      </div>
      <div className="space-y-1">
        {suggestions.map(user => (
          <div
            key={user.id}
            className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid={`suggestion-${user.id}`}
          >
            <button
              onClick={() => navigate(`/profile/${user.id}`)}
              className="flex-shrink-0"
            >
              {user.avatar && !user.avatar.includes('ui-avatars.com') ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div 
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'avatarShimmer 2s ease-in-out infinite'
                  }}
                >
                  <User className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(`/profile/${user.id}`)}
                className="text-left w-full"
              >
                <p className="text-[15px] font-medium text-gray-900 truncate hover:text-[#FF9933] transition-colors">
                  {user.name}
                </p>
              </button>
              {user.mutual_friends_count > 0 && (
                <p className="text-[12px] text-gray-500 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {user.mutual_friends_count} mutual {user.mutual_friends_count === 1 ? 'devotee' : 'devotees'}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              {user.friendship_status === 'none' && (
                <button
                  onClick={() => sendRequest(user.id)}
                  className="p-1.5 rounded-full bg-[#FF9933]/10 hover:bg-[#FF9933]/20 text-[#FF9933] transition-colors"
                  title="Add Devotee"
                  data-testid={`add-friend-${user.id}`}
                >
                  <UserPlus className="w-4 h-4" />
                </button>
              )}
              {user.friendship_status === 'requested' && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Sent
                </span>
              )}
              {user.friendship_status === 'pending' && (
                <span className="text-xs text-[#FF9933] font-medium">Respond</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
